import { useState, useEffect } from 'react'
import { CreateTaskDialog, TaskCreatedSnackbar } from './TasksView.jsx'
import { canCreateTaskFromContact } from './tasks.js'
import {
  Box, Stack, Typography, Button, TextField, Alert, CircularProgress, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { requestManualAction, fetchSuggestions, executeStepAction, fetchConversation, resendConfirmationEmail, fetchCancelReasonCodes, fillTemplateTokens, fetchMessageCustomer, sendMessageCustomer, markMessageCustomerSentInSellerHub, fillMessageCustomerTemplate } from './api.js'
import { messageCustomerAvailable, messageCustomerSendBlock, messageCustomerSentLine, messageCustomerBuyerSource, messageFromTemplate, templatePickerOptions } from './messageCustomer.js'
import EbayDailyLimitNotice from './EbayDailyLimitNotice.jsx'
import { EBAY_DAILY_LIMIT_CODE } from './ebayDailyLimit.js'
import { isActionInputSatisfied, alreadyReturnedSelection, ALREADY_RETURNED_HINT, getEditableNoteDefault, getEditableMessageDefault, reseedActionInputs, formatOrderDateMdy, manualActionErrorMessage, isSingleValidEmail, resendConfirmationErrorMessage } from './suggestionActionInputs.js'
import { groupItemsForTable, isComponentBlockedByKit, toggleKitAwareSelection, RationaleText } from './SuggestionPanel.jsx'
import { ViewCalls } from './ViewCalls.jsx'
import { m365ServiceAddressForCompany } from './constants/companies.js'
import ExpandableTextField from './ExpandableTextField.jsx'
import CreateReturnLabelModal from './CreateReturnLabelModal.jsx'
import RefundModal from './RefundModal.jsx'
import DraggableModal from './DraggableModal.jsx'
import { registerStepSlot, manualStepIndices, SAME_STEP_TYPES } from './stepSlots.js'
import { ZeroDollarRmaBanner, PendingStepChecklist, RmaNumberWaitCaption } from './ReturnLabelPending.jsx'
import { useZeroDollarTiming } from './useReturnLabels.js'
import { isRetiredStep } from '@cxhub/shared'
import { zeroDollarOption, zeroDollarLabelChecked, withZeroDollarLabelChoice, withoutZeroDollarChoice, pendingFromResult, waitsForRmaNumber } from './pendingLabels.js'

// Rendered via the shared ManualActionRow/CancelOrderGroupRow rendering, in
// this array order EXCEPT 'add_note' — pulled out and rendered last, after
// Create Return Shipping Label, so it reads as the catch-all it is (see
// ManualActionsSidebar below). "Create Return Shipping Label" is NOT in this
// array at all — unlike every button here (which reuse whatever the AI
// already proposed this turn when there's a match), it always builds its own
// backend action (create_return_shipping_label) fresh, so it's its own
// dedicated component — see CreateReturnLabelModal.jsx.
const MANUAL_ACTION_BUTTONS = [
  { key: 'return', type: 'process_return_refund', label: 'Create Return' },
  { key: 'replacement', type: 'process_replacement', label: 'Create Replacement' },
  { key: 'cancel', type: 'cancel_order_trq', label: 'Cancel Order' },
  { key: 'cancel_replacement', type: 'cancel_replacement_order_trq', label: 'Cancel Replacement Order' },
  { key: 'remove_hold', type: 'address_confirmation_remove_hold', label: 'Remove Sales Hold' },
  { key: 'update_address', type: 'address_confirmation_update_address', label: 'Update Shipping Address' },
  { key: 'update_shipping_method', type: 'update_shipping_method_trq', label: 'Update Shipping Method' },
  { key: 'resend_label', type: 'resend_return_label', label: 'Resend Return Shipping Label' },
  { key: 'refund_freight', type: 'refund_freight_trq', label: 'Refund Freight' },
  { key: 'refund_tax', type: 'refund_tax_trq', label: 'Refund Tax' },
  { key: 'add_note', type: 'add_order_note', label: 'Add Note to Order' },
]

const ADDRESS_FIELDS = [
  ['shippingName', 'Name'], ['shippingAddress1', 'Address 1'], ['shippingAddress2', 'Address 2'],
  ['shippingCity', 'City'], ['shippingRegion', 'State'], ['shippingPostalCode', 'ZIP'],
]

// The 7 shipping methods TRQ's updateShippingMethod endpoint accepts.
const SHIPPING_METHOD_OPTIONS = ['Regular', 'UPS Ground', 'UPS 2nd Day Air', 'UPS Next Day', 'UPS Next Saver', 'Priority Mail', 'Priority APO']

// Only pre-select the Shipping Method dropdown when the document's raw
// on-file value EXACT-matches one of the 7 options above — unconfirmed
// whether TRQ's raw shippingMethod values line up character-for-character
// with this list (casing, "UPS Ground" vs "Ground", etc.), so a non-matching
// raw value is surfaced as helper text instead of guessed into a selection.
function defaultShippingMethodFor(rawValue) {
  return SHIPPING_METHOD_OPTIONS.includes(rawValue) ? rawValue : ''
}

// select_reason_code fallback catalog (2026-09-28) — a manual action can
// REUSE an existing suggestion's proposed action persisted before the live
// cancel-reason catalog shipped (no inputOptions baked in yet), for
// cancel_order_trq (CancelOrderGroupRow) and cancel_replacement_order_trq
// (ManualActionRow) alike. Fetched once per (conversation, needed) pair;
// `needed` lets each caller skip the fetch entirely when its own action
// already carries real inputOptions.
function useCancelReasonFallback(conversationId, needed) {
  const [options, setOptions] = useState(null)
  useEffect(() => {
    if (!needed || options !== null) return
    let cancelled = false
    fetchCancelReasonCodes(conversationId)
      .then((result) => { if (!cancelled) setOptions(result?.inputOptions ?? []) })
      .catch(() => { if (!cancelled) setOptions([]) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, needed])
  return options ?? []
}

function ShippingAddressFields({ value, onChange }) {
  const address = value?.address ?? {}
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {ADDRESS_FIELDS.map(([field, label]) => (
        <TextField
          key={field}
          size="small"
          label={label}
          value={address[field] ?? ''}
          onChange={(ev) => onChange({ ...value, address: { ...address, [field]: ev.target.value } })}
          InputLabelProps={{ sx: { fontSize: 13 } }}
          inputProps={{ style: { fontSize: 13 } }}
          sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-input': { py: 0.25, px: 0.5 } }}
        />
      ))}
    </Stack>
  )
}

function ItemSelectionTable({ action, value, onChange, readOnly = false, viewerName = null }) {
  const zeroDollarTypical = useZeroDollarTiming(Boolean(zeroDollarOption(action.inputOptions, value?.reasonCode)))
  const selection = value?.items ?? []
  const isPartialOffer = action.type === 'offer_partial_refund_ebay'
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {!action.hideReasonCode && (
        <FormControl size="small" fullWidth error={!value?.reasonCode}>
          <Select
            SelectDisplayProps={{ 'aria-label': action.inputLabel ?? 'Return reason code' }}
            displayEmpty
            value={value?.reasonCode ?? ''}
            sx={{ fontSize: 13, color: 'text.secondary', '& .MuiSelect-select': { py: 0.5 } }}
            onChange={(ev) => onChange({ ...withoutZeroDollarChoice(value ?? { items: [] }), reasonCode: ev.target.value })}
          >
            {(action.inputOptions ?? []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* The $0 RMA (2026-10-29): the banner and the label box. */}
      {zeroDollarOption(action.inputOptions, value?.reasonCode) && (
        <ZeroDollarRmaBanner code={value?.reasonCode} what={action.type === 'process_replacement' ? 'replacement' : 'refund'} typical={zeroDollarTypical}
          checked={zeroDollarLabelChecked(value)} readOnly={readOnly} onChange={(checked) => onChange(withZeroDollarLabelChoice(value, checked, { by: viewerName ?? null }))} />
      )}
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>SKU</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Brand</TableCell>
              {isPartialOffer && <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Price</TableCell>}
              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupItemsForTable(action.itemSelection.allItems).map((it, ri) => {
              const entry = selection.find((x) => x.sku === it.sku)
              const checked = Boolean(entry)
              const description = it.description ?? '—'
              const truncatedDescription = description.length > 30 ? `${description.slice(0, 30)}…` : description
              return (
                <TableRow
                  key={it.sku ?? `${it.description}-${ri}`}
                  sx={it.itemType !== 'component' ? { bgcolor: 'action.hover' } : {}}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={!it.sku || isComponentBlockedByKit(it, action.itemSelection.allItems, selection)}
                      onChange={(ev) => {
                        const isChecked = ev.target.checked
                        const cur = value ?? { reasonCode: '', items: [] }
                        const items = toggleKitAwareSelection(cur, it, action.itemSelection.allItems, isChecked)
                        onChange({ ...cur, items })
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ '&.MuiTableCell-root': { pl: it.itemType === 'component' ? 3 : 1 }, fontSize: 13, color: 'text.secondary' }}>
                    {it.itemType === 'component' ? '↳ ' : ''}{it.sku ?? '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                    <Tooltip title={description}>
                      <span>{truncatedDescription}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{it.brandId ?? '—'}</TableCell>
                  {isPartialOffer && (
                    <TableCell align="right">{it.extendedPrice != null ? `$${Number(it.extendedPrice).toFixed(2)}` : '—'}</TableCell>
                  )}
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={entry?.quantity ?? it.quantity ?? 1}
                      disabled={!checked}
                      sx={{ '& .MuiOutlinedInput-input': { py: 0.25, px: 0.5 } }}
                      inputProps={{ min: 1, max: it.quantity ?? 1, style: { width: 30, textAlign: 'right', fontSize: 13 } }}
                      onChange={(ev) => {
                        const qty = Math.max(1, Math.min(it.quantity ?? 1, Number(ev.target.value) || 1))
                        const cur = value ?? { reasonCode: '', items: [] }
                        const items = cur.items.map((x) => x.sku === it.sku ? { ...x, quantity: qty } : x)
                        onChange({ ...cur, items })
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {alreadyReturnedSelection(action, value).length > 0 && (
        // An item already on an RTN (2026-10-27): no Process for it.
        <Typography variant="body2" color="error" data-testid="already-returned">
          {alreadyReturnedSelection(action, value).join(' ')} {ALREADY_RETURNED_HINT}
        </Typography>
      )}
      {isPartialOffer && (
        <Typography variant="body2" fontWeight={700}>
          Refund Offer: ${selection.reduce((sum, si) => {
            const master = (action.itemSelection.allItems ?? []).find((ai) => ai.sku === si.sku)
            return sum + (master?.unitPrice ?? 0) * (Number(si.quantity) || 0)
          }, 0).toFixed(2)}
        </Typography>
      )}
    </Stack>
  )
}

// Custom gating for the two brand-new hand-built types — isActionInputSatisfied()
// (suggestionActionInputs.js) only knows about the AI's own requiresInput
// convention, which these synthetic actions don't set.
function isSubmittable(buttonType, action, value) {
  if (buttonType === 'address_confirmation_remove_hold') {
    return Boolean((value?.erpOrderId ?? action?.params?.erpOrderId)?.trim?.())
  }
  if (buttonType === 'address_confirmation_update_address') {
    const erpOrderId = value?.erpOrderId ?? action?.params?.erpOrderId
    const addr = value?.address ?? {}
    return Boolean(erpOrderId?.trim?.()) && Boolean(addr.shippingName) && Boolean(addr.shippingAddress1) &&
      Boolean(addr.shippingCity) && Boolean(addr.shippingRegion) && Boolean(addr.shippingPostalCode)
  }
  if (buttonType === 'cancel_replacement_order_trq') {
    return Boolean(value?.replacementId) && Boolean(value?.reasonCode)
  }
  if (buttonType === 'refund_freight_trq' || buttonType === 'refund_tax_trq') {
    return Boolean((value?.editedNote ?? action?.params?.notes)?.trim?.())
  }
  if (buttonType === 'resend_return_label_trq') {
    return Boolean(value?.rmaId) && Boolean(value?.email?.trim())
  }
  if (buttonType === 'update_shipping_method_trq') {
    return Boolean(value?.erpOrderId) && Boolean(value?.id) && Boolean(value?.newShippingMethod) && Boolean(value?.notes?.trim())
  }
  return isActionInputSatisfied(action, value)
}

// The step types started from Other Actions that show the Proposed Steps'
// own step component (2026-10-27, docs/contact-view.md "Other Actions: the
// same step"): the server builds each with the pipeline's grounding and
// builder (services/manual-steps.js), and the window shows the step through
// SuggestionPanel.jsx (stepSlots.js): the disposition dropdown, who pays,
// the refund breakdown with its deduction, Email label to / CC, the packages,
// the follow-on steps, View calls (admin), Process and the reply. A 1AW/AMW
// order's Resend Return Shipping Label is the pipeline's Website label step
// too (resend_return_label), no longer the TRQ-only resend_return_label_trq.

// One step's slot in the window: SuggestionPanel.jsx renders the step into
// it while it is registered.
function StepSlot({ conversationId, suggestionId, index }) {
  const [el, setEl] = useState(null)
  useEffect(() => {
    if (!el) return undefined
    return registerStepSlot({ conversationId, suggestionId, index, element: el })
  }, [el, conversationId, suggestionId, index])
  return (
    <Box ref={setEl} data-testid="manual-step-slot" data-step-index={index}
      sx={{ '&:empty::before': { content: '"Loading the step…"', fontSize: 13, color: 'text.secondary' } }} />
  )
}

// A button whose window shows the step(s) the request added (or the proposed
// step it reused), as the Proposed Steps show them. Opening it asks the
// server for the step and refreshes the suggestion panel so the step is
// there to show.
function ManualStepRow({ conversationId, def, onActionCompleted, offsetIndex = 0, onOpenChange }) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { suggestionId, indices, reused }

  async function load() {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await requestManualAction(conversationId, def.type)
      const indices = manualStepIndices(res)
      if (!res?.suggestionId || !indices.length) throw new Error('Could not load the resulting action')
      setLoaded({ suggestionId: res.suggestionId, indices, reused: Boolean(res.reused) })
      // The suggestion panel reloads the suggestion (no new draft), so the
      // step it renders into this window exists there.
      onActionCompleted?.(def.type)
    } catch (e) {
      setLoadError(manualActionErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  function handleToggle() {
    const next = !expanded
    setExpanded(next)
    onOpenChange?.(next)
    if (next && !loaded && !loading) load()
  }

  return (
    <Box>
      <Button fullWidth size="small" variant="contained" color="primary" onClick={handleToggle} sx={{ justifyContent: 'flex-start' }}>
        {def.label}
      </Button>
      <DraggableModal
        open={expanded}
        onClose={() => { setExpanded(false); onOpenChange?.(false) }}
        title={def.label}
        centered
        offsetIndex={offsetIndex}
        width={560}
        height={600}
      >
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
        {loaded && (
          <Stack spacing={1} data-testid="manual-step-window">
            {loaded.reused && (
              <Alert severity="info" sx={{ py: 0 }}>Reusing the step the AI already proposed for this conversation.</Alert>
            )}
            {loaded.indices.map((index) => (
              <StepSlot key={index} conversationId={conversationId} suggestionId={loaded.suggestionId} index={index} />
            ))}
          </Stack>
        )}
      </DraggableModal>
    </Box>
  )
}

// A single button + its own DraggableModal. Owns its own load/input/check/
// process state, independent of every other button — so the CSR can have
// several open at once without them interfering. offsetIndex/onOpenChange
// come from ManualActionsSidebar's shared stagger registry (see there) —
// onOpenChange also collapses the Other Actions panel itself the moment
// this opens.
function ManualActionRow({ conversationId, def, onActionCompleted, isAdmin = false, offsetIndex = 0, onOpenChange, channel = null, isTestingPeriodTrqRole = false }) {
  // resend_return_label's button/label stays the same for every role — only
  // WHICH backend action type it wires to changes for 1AW/AMW, which has no
  // eBay side and so needs the TRQ-native flow instead of the eBay-return-
  // search-shaped one. Every def.type-driven branch below uses this instead.
  const effectiveType = def.type === 'resend_return_label' && isTestingPeriodTrqRole ? 'resend_return_label_trq' : def.type
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { suggestionId, actionIndex, action, reused }
  const [value, setValue] = useState(null)
  const [processState, setProcessState] = useState(null) // { status: 'processing'|'success'|'failed', error }

  async function load() {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await requestManualAction(conversationId, effectiveType)
      const list = await fetchSuggestions(conversationId)
      const suggestion = list.find((s) => s.id === res.suggestionId)
      const action = suggestion?.proposedActions?.[res.actionIndex]
      if (!suggestion || !action) throw new Error('Could not load the resulting action')
      setLoaded({ suggestionId: res.suggestionId, actionIndex: res.actionIndex, action, reused: res.reused })
      const seeded = reseedActionInputs(suggestion.proposedActions, {})
      let initial = seeded[res.actionIndex] ?? null
      // Hand-built types carry no AI-resolved seed via reseedActionInputs — pull
      // their editable starting values directly from the action's own params.
      if (effectiveType === 'address_confirmation_remove_hold') {
        initial = { erpOrderId: action.params?.erpOrderId ?? '', holdType: action.params?.holdType ?? 'SALES', note: action.params?.note ?? '' }
      } else if (effectiveType === 'address_confirmation_update_address') {
        initial = {
          erpOrderId: action.params?.erpOrderId ?? '',
          address: {
            shippingName: action.params?.shippingName ?? '',
            shippingAddress1: action.params?.shippingAddress1 ?? '',
            shippingAddress2: action.params?.shippingAddress2 ?? '',
            shippingCity: action.params?.shippingCity ?? '',
            shippingRegion: action.params?.shippingRegion ?? '',
            shippingPostalCode: action.params?.shippingPostalCode ?? '',
          },
        }
      } else if (effectiveType === 'cancel_replacement_order_trq') {
        // The server already sorts replacementOptions newest-first — default
        // to that one, same convention ItemSelectionTable's reason code seeds
        // from action.inputDefault.
        initial = { replacementId: action.params?.replacementOptions?.[0]?.id ?? '', reasonCode: '' }
      } else if (effectiveType === 'add_order_note') {
        // Merge onto the reseeded value — that's where editedNote's own
        // default comes from; this just also seeds which document the note
        // posts to (1AW/AMW's dropdown; a no-op elsewhere since
        // childOrderOptions is absent and the dropdown never renders).
        initial = { ...(seeded[res.actionIndex] ?? {}), erpOrderId: action.params?.erpOrderId ?? '' }
      } else if (effectiveType === 'resend_return_label_trq') {
        initial = { rmaId: action.params?.rmaId ?? '', email: action.params?.email ?? '' }
      } else if (effectiveType === 'update_shipping_method_trq') {
        // Default document = the parent order (documentOptions[0]) — same
        // "server already sorts newest-first, default to the first entry"
        // convention as cancel_replacement_order_trq above.
        const defaultDoc = action.params?.documentOptions?.[0] ?? null
        initial = {
          erpOrderId: defaultDoc?.erpOrderId ?? action.params?.erpOrderId ?? '',
          id: defaultDoc?.id ?? action.params?.id ?? '',
          newShippingMethod: defaultShippingMethodFor(defaultDoc?.shippingMethod),
          notes: action.params?.notes ?? '',
        }
      }
      setValue(initial)
    } catch (e) {
      setLoadError(manualActionErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  function handleToggle() {
    const next = !expanded
    setExpanded(next)
    onOpenChange?.(next)
    if (next && !loaded && !loading) load()
  }

  async function handleProcess() {
    if (!loaded) return
    setProcessState({ status: 'processing', error: null })
    try {
      const res = await executeStepAction(loaded.suggestionId, loaded.actionIndex, { [loaded.actionIndex]: value }, { source: 'manual_panel' })
      if (res.success) {
        setProcessState({ status: 'success', result: res.result ?? null, error: null })
        onActionCompleted?.(def.type)
      } else {
        setProcessState({ status: 'failed', error: res.error ?? 'Action failed' })
      }
    } catch (e) {
      setProcessState({ status: 'failed', error: e.message })
    }
  }

  const action = loaded?.action
  const decided = processState?.status === 'success'
  // select_reason_code fallback catalog — only relevant for
  // cancel_replacement_order_trq, and only fetched once this row has
  // actually loaded a (possibly stale, "reused") action.
  const cancelReplacementFallbackOptions = useCancelReasonFallback(
    conversationId,
    def.type === 'cancel_replacement_order_trq' && Boolean(action) && !(action.inputOptions?.length)
  )
  const cancelReplacementReasonOptions = action?.inputOptions?.length ? action.inputOptions : cancelReplacementFallbackOptions

  return (
    <Box>
      <Button
        fullWidth
        size="small"
        variant="contained"
        color="primary"
        onClick={handleToggle}
        sx={{ justifyContent: 'flex-start' }}
      >
        {def.label}
      </Button>
      <DraggableModal
        open={expanded}
        onClose={() => { setExpanded(false); onOpenChange?.(false) }}
        title={def.label}
        centered
        offsetIndex={offsetIndex}
        width={520}
        height={520}
      >
          {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
          {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
          {action && (
            <Stack spacing={1}>
              {loaded.reused && (
                <Alert severity="info" sx={{ py: 0 }}>Reusing the step the AI already proposed for this conversation.</Alert>
              )}
              {def.type === 'address_confirmation_remove_hold' && action.params?.holdReason && (
                <Typography variant="body2" sx={{ fontWeight: 700, bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                  Hold reason: {action.params.holdReason}
                </Typography>
              )}
              {(def.type === 'refund_freight_trq' || def.type === 'refund_tax_trq') && action.params?.refundAmount != null && (
                <Typography variant="body2" sx={{ fontWeight: 700, bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                  Refund total: ${Number(action.params.refundAmount).toFixed(2)}
                </Typography>
              )}
              <Typography variant="body2">{action.label}</Typography>
              <RationaleText rationale={action.rationale} />

              {def.type === 'address_confirmation_remove_hold' && (
                <Stack spacing={1}>
                  <TextField size="small" label="Order ID" disabled value={value?.erpOrderId ?? ''} onChange={(ev) => setValue({ ...value, erpOrderId: ev.target.value })} />
                  <TextField size="small" label="Hold type" disabled value={value?.holdType ?? ''} onChange={(ev) => setValue({ ...value, holdType: ev.target.value })} />
                  <ExpandableTextField size="small" label="Note" multiline minRows={2} fullWidth value={value?.note ?? ''} onChange={(ev) => setValue({ ...value, note: ev.target.value })} />
                </Stack>
              )}

              {def.type === 'address_confirmation_update_address' && (
                <Stack spacing={1}>
                  {action.params?.documentOptions?.length > 0 && (
                    <FormControl size="small" sx={{ maxWidth: 320 }}>
                      <InputLabel>Document</InputLabel>
                      <Select
                        label="Document"
                        value={value?.erpOrderId ?? ''}
                        onChange={(ev) => {
                          const opt = action.params.documentOptions.find((o) => o.erpOrderId === ev.target.value)
                          if (!opt) return
                          setValue({ ...(value ?? {}), erpOrderId: opt.erpOrderId, address: { ...opt.address } })
                        }}
                      >
                        {action.params.documentOptions.map((opt) => (
                          <MenuItem key={opt.erpOrderId} value={opt.erpOrderId}>
                            {opt.erpOrderId} — {opt.documentType}{opt.date ? ` — ${formatOrderDateMdy(opt.date) ?? opt.date}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <ShippingAddressFields value={value} onChange={setValue} />
                </Stack>
              )}

              {def.type === 'cancel_replacement_order_trq' && (
                <Stack spacing={1}>
                  <FormControl size="small" sx={{ maxWidth: 320 }}>
                    <InputLabel>Replacement order</InputLabel>
                    <Select
                      label="Replacement order"
                      value={value?.replacementId ?? ''}
                      onChange={(ev) => setValue({ ...(value ?? {}), replacementId: ev.target.value })}
                    >
                      {(action.params?.replacementOptions ?? []).map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>{opt.erpOrderId} — {formatOrderDateMdy(opt.orderDate) ?? 'date unknown'}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    select
                    size="small"
                    label="Console reason code"
                    value={value?.reasonCode ?? ''}
                    onChange={(ev) => setValue({ ...(value ?? {}), reasonCode: ev.target.value })}
                    required
                    error={!value?.reasonCode}
                    helperText={!value?.reasonCode ? 'Required before processing' : ''}
                    sx={{ minWidth: 240, maxWidth: 320 }}
                  >
                    {cancelReplacementReasonOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}

              {effectiveType === 'add_order_note' && action.params?.childOrderOptions?.length > 0 && (
                <FormControl size="small" sx={{ maxWidth: 320 }}>
                  <InputLabel>Order</InputLabel>
                  <Select
                    label="Order"
                    value={value?.erpOrderId ?? ''}
                    onChange={(ev) => setValue({ ...(value ?? {}), erpOrderId: ev.target.value })}
                  >
                    {action.params.childOrderOptions.map((opt) => (
                      <MenuItem key={opt.erpOrderId} value={opt.erpOrderId}>{opt.erpOrderId} ({opt.documentType})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {effectiveType === 'resend_return_label_trq' && (
                <Stack spacing={1}>
                  <FormControl size="small" sx={{ maxWidth: 320 }}>
                    <InputLabel>RMA</InputLabel>
                    <Select
                      label="RMA"
                      value={value?.rmaId ?? ''}
                      onChange={(ev) => setValue({ ...(value ?? {}), rmaId: ev.target.value })}
                    >
                      {(action.params?.rmaOptions ?? []).map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>{opt.erpOrderId} — {formatOrderDateMdy(opt.orderDate) ?? 'date unknown'}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Email"
                    value={value?.email ?? ''}
                    onChange={(ev) => setValue({ ...(value ?? {}), email: ev.target.value })}
                  />
                  <FormControlLabel
                    sx={{ display: 'block', ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={value?.ccInternalCopy ?? true}
                        onChange={(ev) => setValue({ ...(value ?? {}), ccInternalCopy: ev.target.checked })}
                      />
                    }
                    label="Also send a copy to"
                  />
                  {(value?.ccInternalCopy ?? true) && (
                    <TextField
                      size="small"
                      label="Cc address"
                      type="email"
                      // Same default/override pattern as SuggestionPanel.jsx's
                      // send_return_label_email field, reusing the exact same
                      // helper so the two defaults never drift apart.
                      value={value?.ccAddress ?? m365ServiceAddressForCompany(action.params?.company)}
                      onChange={(ev) => setValue({ ...(value ?? {}), ccAddress: ev.target.value })}
                    />
                  )}
                </Stack>
              )}

              {effectiveType === 'update_shipping_method_trq' && (() => {
                const selectedDoc = (action.params?.documentOptions ?? []).find((o) => o.erpOrderId === value?.erpOrderId)
                const rawShippingMethod = selectedDoc?.shippingMethod ?? null
                const methodMatchesRaw = rawShippingMethod && SHIPPING_METHOD_OPTIONS.includes(rawShippingMethod)
                return (
                  <Stack spacing={1}>
                    <FormControl size="small" sx={{ maxWidth: 320 }}>
                      <InputLabel>Document</InputLabel>
                      <Select
                        label="Document"
                        value={value?.erpOrderId ?? ''}
                        onChange={(ev) => {
                          const opt = (action.params?.documentOptions ?? []).find((o) => o.erpOrderId === ev.target.value)
                          if (!opt) return
                          setValue({
                            ...(value ?? {}),
                            erpOrderId: opt.erpOrderId,
                            id: opt.id,
                            newShippingMethod: defaultShippingMethodFor(opt.shippingMethod),
                          })
                        }}
                      >
                        {(action.params?.documentOptions ?? []).map((opt) => (
                          <MenuItem key={opt.erpOrderId} value={opt.erpOrderId}>
                            {opt.erpOrderId} — {opt.documentType}{opt.date ? ` — ${formatOrderDateMdy(opt.date) ?? opt.date}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ maxWidth: 320 }}>
                      <InputLabel>Shipping Method</InputLabel>
                      <Select
                        label="Shipping Method"
                        value={value?.newShippingMethod ?? ''}
                        onChange={(ev) => setValue({ ...(value ?? {}), newShippingMethod: ev.target.value })}
                      >
                        {SHIPPING_METHOD_OPTIONS.map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {rawShippingMethod && !methodMatchesRaw && (
                      <Typography variant="caption" color="text.secondary">On file: {rawShippingMethod}</Typography>
                    )}
                    <ExpandableTextField
                      size="small"
                      label="Note"
                      multiline
                      minRows={2}
                      fullWidth
                      value={value?.notes ?? ''}
                      onChange={(ev) => setValue({ ...(value ?? {}), notes: ev.target.value })}
                      required
                      error={!value?.notes?.trim()}
                      helperText={!value?.notes?.trim() ? 'Required before processing' : ''}
                    />
                  </Stack>
                )
              })()}

              {action.itemSelection && (
                <ItemSelectionTable action={action} value={value} onChange={(v) => { setValue(v); setCheckState(null) }} />
              )}
              {action.shippingSelection && (
                <Stack spacing={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: 13 }}>Shipping method</InputLabel>
                    <Select
                      label="Shipping method"
                      value={value?.shippingMethod ?? ''}
                      sx={{ fontSize: 13, color: 'text.secondary', '& .MuiSelect-select': { py: 0.5 } }}
                      onChange={(ev) => setValue({ ...(value ?? {}), shippingMethod: ev.target.value })}
                    >
                      {action.shippingSelection.methodOptions.map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <ShippingAddressFields value={value} onChange={setValue} />
                </Stack>
              )}
              {getEditableNoteDefault(action) !== null && (
                <ExpandableTextField
                  size="small"
                  label="Order note (editable)"
                  multiline
                  minRows={3}
                  maxRows={3}
                  fullWidth
                  value={value?.editedNote ?? ''}
                  onChange={(ev) => setValue({ ...(value ?? {}), editedNote: ev.target.value })}
                  inputProps={{ style: { resize: 'vertical', overflow: 'auto' } }}
                />
              )}

              {processState?.status === 'failed' && <Alert severity="error">{processState.error}</Alert>}
              {/* Same success presentation as SuggestionPanel.jsx's Process
                  result (lines ~1256-1270): green-tinted box, plain
                  "Successfully processed" for non-admin, raw JSON result for
                  admin — same isAdmin check, no new admin-detection logic. */}
              {processState?.status === 'success' && (
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.success.light, 0.18) }}>
                  {isAdmin ? (
                    <>
                      <Typography variant="caption" color="success.dark" fontWeight={600} display="block" sx={{ mb: 0.25 }}>Step result</Typography>
                      <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(processState.result, null, 2)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="success.dark" fontWeight={600} display="block">Successfully processed</Typography>
                  )}
                </Box>
              )}


              <Stack direction="row" spacing={1}>
                  <Button
                    size="small" variant="contained"
                    disabled={decided || processState?.status === 'processing' || !isSubmittable(effectiveType, action, value)}
                    onClick={handleProcess}
                    startIcon={processState?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {processState?.status === 'processing' ? 'Processing…' : 'Process'}
                  </Button>
              </Stack>
            </Stack>
          )}
      </DraggableModal>
    </Box>
  )
}

// "Cancel Order" doesn't fit ManualActionRow's one-button-one-action model:
// actions.js's cancellationActions() always returns a CLUSTER of 1-4 related
// actions together (e.g. select_reason_code + cancel_order_trq +
// approve_ebay_cancellation/reject_ebay_cancellation + send_ebay_member_message),
// tagged with group: 'cancellation' — routes/ai.js's manual-actions route
// returns the whole cluster as actionIndices. This renders every action in
// the cluster together, each with its own Process button/result (or, for the
// advisory-only outcomes — advise_already_canceled/advise_cannot_cancel/
// advise_manual_review/no_action_needed — just its label/rationale, no
// button, same as SuggestionPanel.jsx shows for those today).
function CancelOrderGroupRow({ conversationId, def, onActionCompleted, isAdmin = false, offsetIndex = 0, onOpenChange }) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { suggestionId, proposedActions, groupIndices, reused }
  const [actionInputs, setActionInputs] = useState({})
  const [stepStates, setStepStates] = useState({}) // { [index]: { status: 'processing'|'success'|'failed', result, error } }

  async function load() {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await requestManualAction(conversationId, 'cancel_order_trq')
      const list = await fetchSuggestions(conversationId)
      const suggestion = list.find((s) => s.id === res.suggestionId)
      const groupIndices = res.actionIndices ?? [res.actionIndex]
      if (!suggestion || !groupIndices.length) throw new Error('Could not load the resulting action')
      setLoaded({ suggestionId: res.suggestionId, proposedActions: suggestion.proposedActions ?? [], groupIndices, reused: res.reused })
      // reseedActionInputs already generically handles select_reason_code's
      // numeric default (a.inputDefault) and send_ebay_member_message's
      // editable-message default (getEditableMessageDefault) — the same
      // mechanism SuggestionPanel.jsx uses for these exact action types.
      setActionInputs(reseedActionInputs(suggestion.proposedActions, {}))
    } catch (e) {
      setLoadError(manualActionErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  function handleToggle() {
    const next = !expanded
    setExpanded(next)
    onOpenChange?.(next)
    if (next && !loaded && !loading) load()
  }

  // Passes the FULL actionInputs map (keyed by each action's real index in
  // proposedActions), not just this one index's value — action-executor.js's
  // cancel_order_trq branch falls back to the select_reason_code action's own
  // actionInputs slot (found by scanning proposedActions for that type) when
  // this step's own slot is empty, exactly like SuggestionPanel.jsx already
  // does for every proposed step.
  async function handleProcess(index) {
    if (!loaded) return
    setStepStates((prev) => ({ ...prev, [index]: { status: 'processing', result: null, error: null } }))
    try {
      const res = await executeStepAction(loaded.suggestionId, index, actionInputs, { source: 'manual_panel' })
      if (res.success) {
        setStepStates((prev) => ({ ...prev, [index]: { status: 'success', result: res.result ?? null, error: null } }))
        onActionCompleted?.(def.type)
      } else {
        setStepStates((prev) => ({ ...prev, [index]: { status: 'failed', result: null, error: res.error ?? 'Action failed' } }))
      }
    } catch (e) {
      setStepStates((prev) => ({ ...prev, [index]: { status: 'failed', result: null, error: e.message } }))
    }
  }

  const groupActions = loaded ? loaded.groupIndices.map((idx) => ({ idx, action: loaded.proposedActions[idx] })) : []
  // select_reason_code fallback catalog — only relevant when this group's
  // own select_reason_code step is missing inputOptions (a "reused",
  // pre-deploy suggestion).
  const cancelReasonStep = groupActions.find(({ action }) => action.type === 'select_reason_code')?.action ?? null
  const cancelReasonFallbackOptions = useCancelReasonFallback(
    conversationId,
    Boolean(cancelReasonStep) && !(cancelReasonStep.inputOptions?.length)
  )

  return (
    <Box>
      <Button
        fullWidth
        size="small"
        variant="contained"
        color="primary"
        onClick={handleToggle}
        sx={{ justifyContent: 'flex-start' }}
      >
        {def.label}
      </Button>
      <DraggableModal
        open={expanded}
        onClose={() => { setExpanded(false); onOpenChange?.(false) }}
        title={def.label}
        centered
        offsetIndex={offsetIndex}
        width={560}
        height={560}
      >
          {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
          {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
          {groupActions.length > 0 && (
            <Stack spacing={1.5}>
              {loaded.reused && (
                <Alert severity="info" sx={{ py: 0 }}>Reusing the steps the AI already proposed for this conversation.</Alert>
              )}
              {/* A step not used any more (2026-11-03, the replacement's old
                  label step) on reused steps: not shown. */}
              {groupActions.filter(({ action }) => !isRetiredStep(action)).map(({ idx, action }) => {
                const ss = stepStates[idx] ?? { status: 'idle' }
                const value = actionInputs[idx]
                const showButton = Boolean(action.targetSystem)
                const editableMessage = getEditableMessageDefault(action)
                const editableNote = getEditableNoteDefault(action)
                return (
                  <Box key={idx} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {action.targetSystem && <Typography variant="caption" color="primary" fontWeight={600}>{action.targetSystem}</Typography>}
                        <Typography variant="body2" sx={{ flex: 1 }}>{action.label}</Typography>
                        {/* View call (2026-10-27): the shared link and
                            modal (ViewCalls.jsx), admin only as before. */}
                        {isAdmin && action.apiCall && (
                          <ViewCalls title={action.label} testId="manual-cancel-view-call" calls={[{ label: null, call: action.apiCall }]} />
                        )}
                      </Stack>
                      <RationaleText rationale={action.rationale} />

                      {action.type === 'select_reason_code' && (
                        <TextField
                          select
                          size="small"
                          label={action.inputLabel ?? 'Reason code'}
                          value={actionInputs[idx] ?? ''}
                          onChange={(ev) => setActionInputs((prev) => ({ ...prev, [idx]: ev.target.value }))}
                          required
                          error={!actionInputs[idx]}
                          helperText={!actionInputs[idx] ? 'Required before processing' : ''}
                          sx={{ minWidth: 240 }}
                        >
                          {(action.inputOptions?.length ? action.inputOptions : cancelReasonFallbackOptions).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </TextField>
                      )}

                      {action.itemSelection && (
                        <ItemSelectionTable
                          action={action}
                          value={value}
                          readOnly={ss.status === 'success' || ss.status === 'processing'}
                          onChange={(next) => setActionInputs((prev) => ({ ...prev, [idx]: next }))}
                        />
                      )}

                      {editableMessage !== null && (
                        <ExpandableTextField
                          isCustomerReply
                          size="small"
                          label="Message to buyer (editable)"
                          value={value?.editedMessage ?? ''}
                          onChange={(ev) => setActionInputs((prev) => ({ ...prev, [idx]: { ...(prev[idx] ?? {}), editedMessage: ev.target.value } }))}
                          disabled={ss.status === 'processing'}
                          multiline
                          minRows={4}
                          fullWidth
                        />
                      )}

                      {editableNote !== null && (
                        <ExpandableTextField
                          size="small"
                          label="Order note (editable)"
                          value={value?.editedNote ?? ''}
                          onChange={(ev) => setActionInputs((prev) => ({ ...prev, [idx]: { ...(prev[idx] ?? {}), editedNote: ev.target.value } }))}
                          disabled={ss.status === 'processing'}
                          multiline
                          minRows={3}
                          maxRows={3}
                          fullWidth
                          inputProps={{ style: { resize: 'vertical', overflow: 'auto' } }}
                        />
                      )}

                      {ss.status === 'failed' && <Alert severity="error" sx={{ py: 0 }}>{ss.error}</Alert>}
                      {ss.status === 'processing' && waitsForRmaNumber(action) && <RmaNumberWaitCaption />}
                      {/* Waiting on the OMS API (2026-10-29): the checklist. */}
                      {ss.status === 'success' && pendingFromResult(ss.result) && (
                        <PendingStepChecklist row={pendingFromResult(ss.result)} compact />
                      )}
                      {ss.status === 'success' && !pendingFromResult(ss.result) && (
                        <Box sx={{ p: 1, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.success.light, 0.18) }}>
                          {isAdmin ? (
                            <>
                              <Typography variant="caption" color="success.dark" fontWeight={600} display="block" sx={{ mb: 0.25 }}>Step result</Typography>
                              <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {JSON.stringify(ss.result, null, 2)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="success.dark" fontWeight={600} display="block">Successfully processed</Typography>
                          )}
                        </Box>
                      )}

                      {showButton && (
                        <Button
                          size="small" variant="contained"
                          disabled={ss.status === 'processing' || ss.status === 'success' || !isActionInputSatisfied(action, value)}
                          onClick={() => handleProcess(idx)}
                          startIcon={ss.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {ss.status === 'processing' ? 'Processing…' : ss.status === 'success' ? 'Processed' : 'Process'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          )}
      </DraggableModal>
    </Box>
  )
}

// Slide-out sidebar (App.jsx renders the fixed-position wrapper + resize
// handle + arrow tab) letting a CSR trigger any of the order-action flows
// even when the AI didn't propose it this turn. Each button independently
// reuses a matching proposedActions[] entry already on the conversation's
// current suggestion, or grounds + builds one on demand — see routes/ai.js's
// /manual-actions route.
// eBay Seller Central: direct eBay-side writes with no console equivalent.
// Both buttons open the shared RefundModal (parameterized per button).
const EBAY_SELLER_CENTRAL_BUTTONS = [
  {
    key: 'create_refund', actionType: 'create_ebay_refund', label: 'Create Refund',
    instructionalText: 'Use this option when a customer has NOT created an eBay Gen, but we\'ve determined a part is scrap and want to issue a refund.',
    requiresReturnId: false,
  },
  {
    key: 'issue_refund', actionType: 'issue_ebay_return_refund', label: 'Issue Refund',
    instructionalText: 'Use this option when an eBay Gen already exists, but we\'ve determined a part is scrap and want to issue a refund.',
    requiresReturnId: true,
  },
]

// Shared multi-instance stagger registry: every button below opens its own
// DraggableModal, and any combination of them can end up open at once, so
// this tracks the OPEN ORDER (by each button's own stable key) across all of
// them and hands back each one's position in that order as its
// offsetIndex — the same convention OrderNotesWindow.jsx/App.jsx's own
// notesWindows array uses, just keyed by button identity instead of an
// incrementing counter since these aren't literally a list of windows.
function useModalStagger() {
  const [openKeys, setOpenKeys] = useState([])
  function handleOpenChange(key, isOpen) {
    setOpenKeys((prev) => {
      if (isOpen) return prev.includes(key) ? prev : [...prev, key]
      return prev.filter((k) => k !== key)
    })
  }
  function offsetIndexFor(key) {
    const i = openKeys.indexOf(key)
    return i === -1 ? 0 : i
  }
  return { handleOpenChange, offsetIndexFor }
}

// "Resend Order Confirmation Email"/"Resend Shipping Confirmation Email" —
// identical apart from kind/wording, a single direct TRQ write (not routed
// through the proposed-action/suggestion machinery CreateReturnLabelModal.jsx
// uses), so one component covers both call sites below.
function ResendConfirmationEmailModal({ conversationId, kind, erpOrderId, prefillEmail, offsetIndex = 0, onOpenChange }) {
  const [expanded, setExpanded] = useState(false)
  const [email, setEmail] = useState('')
  const [state, setState] = useState(null) // { status: 'processing'|'success'|'failed', error }
  const noun = kind === 'order' ? 'Order' : 'Shipping'

  function openModal() {
    setExpanded(true)
    onOpenChange?.(true)
    setEmail(prefillEmail ?? '')
    setState(null)
  }
  function closeModal() {
    setExpanded(false)
    onOpenChange?.(false)
    setState(null)
  }
  function handleToggle() {
    if (expanded) closeModal(); else openModal()
  }
  async function handleProcess() {
    setState({ status: 'processing', error: null })
    try {
      await resendConfirmationEmail(conversationId, kind, email.trim())
      setState({ status: 'success', error: null })
    } catch (e) {
      // Email left intact (not reset) so the agent can fix it and retry.
      setState({ status: 'failed', error: resendConfirmationErrorMessage(e) })
    }
  }

  return (
    <Box>
      <Tooltip title={erpOrderId ? '' : 'No order is linked to this contact'} disableHoverListener={Boolean(erpOrderId)}>
        <span>
          <Button
            fullWidth
            size="small"
            variant="contained"
            color="primary"
            disabled={!erpOrderId}
            onClick={handleToggle}
            sx={{ justifyContent: 'flex-start' }}
          >
            Resend {noun} Confirmation Email
          </Button>
        </span>
      </Tooltip>
      <DraggableModal
        open={expanded}
        onClose={closeModal}
        title={`Resend ${noun} Confirmation Email`}
        centered
        offsetIndex={offsetIndex}
        width={420}
        height={260}
        actions={
          state?.status !== 'success' ? (
            <Button
              variant="contained"
              disabled={state?.status === 'processing' || !isSingleValidEmail(email)}
              onClick={handleProcess}
              startIcon={state?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {state?.status === 'processing' ? 'Processing…' : 'Process'}
            </Button>
          ) : null
        }
      >
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Send to"
            value={email}
            disabled={state?.status === 'success'}
            onChange={(e) => setEmail(e.target.value)}
          />
          {state?.status === 'success' && (
            <Alert severity="success">{noun} confirmation email sent to {email}.</Alert>
          )}
          {state?.status === 'failed' && (
            <Alert severity="error">The {noun.toLowerCase()} confirmation email could not be sent. {state.error}</Alert>
          )}
        </Stack>
      </DraggableModal>
    </Box>
  )
}

// "Message the customer" (eBay orders only since 2026-10-30;
// docs/contact-view.md, ui/src/messageCustomer.js): shown only when the
// contact's order is an eBay order. The server builds the message to the
// buyer (the Sales Hold Address prefill, the signature, the address block,
// the client's eBay folders for the template picker) and Send Message sends
// it on eBay. It is recorded on this contact (the thread and the Activity
// Log); it never closes the contact or touches its suggestion, so the main
// reply and steps can still be sent and processed.
// 2026-11-02 (Jenn): the Template picker always shows; on a contact with no
// AI suggestion a picked template is filled from the order by
// .../message-customer/templates/:id/fill, and any INSERT placeholder left
// keeps Send disabled. eBay's daily limit shows the step's Seller Hub notice
// with "Sent response through eBay Portal" (.../message-customer/mark-sent).
function MessageCustomerModal({ conversationId, onActionCompleted, offsetIndex = 0, onOpenChange }) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [loaded, setLoaded] = useState(null) // { step, templateFillSuggestionId }
  const [message, setMessage] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [state, setState] = useState(null) // { status: 'processing'|'success'|'failed', error, sent, limit }

  async function load() {
    setLoading(true)
    setLoadError(null)
    setLoaded(null)
    try {
      const res = await fetchMessageCustomer(conversationId)
      if (!res?.step) throw new Error('Could not load the message')
      setLoaded({ step: res.step, templateFillSuggestionId: res.templateFillSuggestionId ?? null })
      setMessage(res.step.params?.messageText ?? '')
      setTemplateId('')
    } catch (e) {
      setLoadError(manualActionErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }
  function close() {
    setExpanded(false)
    onOpenChange?.(false)
  }
  function handleToggle() {
    if (expanded) { close(); return }
    setExpanded(true)
    onOpenChange?.(true)
    setState(null)
    load()
  }
  async function pickTemplate(id) {
    setTemplateId(id)
    if (!id || !loaded) return
    try {
      const filled = loaded.templateFillSuggestionId
        ? await fillTemplateTokens(loaded.templateFillSuggestionId, id)
        : await fillMessageCustomerTemplate(conversationId, id)
      setMessage(messageFromTemplate(filled?.content ?? '', { addressBlock: loaded.step.params?.addressBlock, signature: loaded.step.params?.signature }))
    } catch (e) {
      setState({ status: 'failed', error: e.message })
    }
  }
  async function handleSend() {
    setState({ status: 'processing', error: null })
    try {
      const res = await sendMessageCustomer(conversationId, message)
      if (res?.sent) {
        setState({ status: 'success', error: null, sent: res })
        // Refreshes the thread and the Activity Log; never the reply box.
        onActionCompleted?.('message_customer')
      } else if (res?.errorCode === EBAY_DAILY_LIMIT_CODE) {
        // The step's Seller Hub state: copy, send in Seller Hub, then record it.
        setState({ status: 'failed', error: res.error, limit: { messageText: res.messageText ?? message, boxText: message, buyerId: res.buyerId ?? buyerId, itemId: res.itemId ?? null, erpOrderId: res.erpOrderId ?? null } })
      } else {
        setState({ status: 'failed', error: res?.error ?? 'The message could not be sent' })
      }
    } catch (e) {
      setState({ status: 'failed', error: e.message })
    }
  }
  // "Sent response through eBay Portal": the box as sent in Seller Hub (the
  // refused text when the box was not changed since). Errors show in the
  // notice.
  async function handleMarkSent() {
    const limit = state?.limit
    const res = await markMessageCustomerSentInSellerHub(conversationId, message === limit?.boxText ? '' : message)
    setState({ status: 'success', error: null, sent: res })
    onActionCompleted?.('message_customer')
  }

  const step = loaded?.step
  const buyerId = step?.params?.buyerId ?? null
  const buyerSource = messageCustomerBuyerSource(step?.params?.buyerIdSource)
  const options = templatePickerOptions(step?.params?.templateOptions)
  const sendBlock = messageCustomerSendBlock({ message, buyerId, status: state?.status })
  return (
    <Box>
      <Button fullWidth size="small" variant="contained" color="primary" onClick={handleToggle}
        sx={{ justifyContent: 'flex-start' }} data-testid="message-customer-button">
        Message the customer
      </Button>
      <DraggableModal open={expanded} onClose={close} title="Message the customer" centered offsetIndex={offsetIndex} width={560} height={560}
        actions={step && state?.status !== 'success' ? (
          <Button variant="contained" data-testid="message-customer-send" disabled={Boolean(sendBlock)}
            onClick={handleSend} startIcon={state?.status === 'processing' ? <CircularProgress size={14} color="inherit" /> : null}>
            {state?.status === 'processing' ? 'Sending…' : 'Send Message'}
          </Button>
        ) : null}>
        {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>}
        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
        {step && (
          <Stack spacing={1} data-testid="message-customer-modal">
            <Typography variant="body2">{step.label}</Typography>
            {buyerSource && <Typography variant="caption" color="text.secondary" data-testid="message-customer-buyer-source">{buyerSource}</Typography>}
            <FormControl size="small" fullWidth>
              <InputLabel id="message-customer-template-label">Template</InputLabel>
              <Select labelId="message-customer-template-label" label="Template" value={templateId} onChange={(e) => pickTemplate(e.target.value)}
                disabled={state?.status === 'success' || options.length === 0} SelectDisplayProps={{ 'data-testid': 'message-customer-template' }}>
                <MenuItem value=""><em>{step.params?.template?.title ? `As built: ${step.params.template.title}` : (options.length ? 'No template' : 'No eBay templates for this brand')}</em></MenuItem>
                {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.folder} / {o.title}</MenuItem>)}
              </Select>
            </FormControl>
            <ExpandableTextField size="small" label="Message to buyer (editable)" multiline minRows={8} fullWidth
              value={message} onChange={(e) => setMessage(e.target.value)} disabled={state?.status === 'success' || state?.status === 'processing'}
              inputProps={{ 'data-testid': 'message-customer-text', style: { resize: 'vertical', overflow: 'auto' } }} />
            {sendBlock && state?.status !== 'processing' && state?.status !== 'success' && (
              <Typography variant="caption" color="text.secondary" data-testid="message-customer-send-reason">{sendBlock}</Typography>
            )}
            {state?.status === 'success' && <Alert severity="success" data-testid="message-customer-sent">{messageCustomerSentLine(state.sent)}</Alert>}
            {state?.status === 'failed' && state.limit && (
              <EbayDailyLimitNotice text={state.error} messageText={state.limit.messageText}
                action={{ params: { buyerId: state.limit.buyerId, erpOrderId: state.limit.erpOrderId, itemId: state.limit.itemId } }}
                onMarkSent={handleMarkSent} />
            )}
            {state?.status === 'failed' && !state.limit && <Alert severity="error" data-testid="message-customer-error">{state.error}</Alert>}
          </Stack>
        )}
      </DraggableModal>
    </Box>
  )
}

// onModalOpen: called the moment ANY button below opens its modal — App.jsx
// wires this to collapse the Other Actions panel itself, so the panel never
// sits open behind the modal it just launched.
// orderSummaryCustomerEmail: the ACTIVE conversation's already-loaded
// order-summary customerEmail (not conversation.customer.email, which is
// often null for eBay/Website contacts) — bubbled up from ConversationPane's
// own order-summary fetch via App.jsx's Shell (see
// handleOrderSummaryEmailChange there). Deliberately NOT fetched again here:
// this sidebar and the message pane it sits beside are separate components,
// but both fetching order-summary independently double-calls it (and, worse,
// races the SAME endpoint's mocked/observed call count in tests and any
// server-side per-call accounting) for every open conversation.
// taskViewer / canCreateTasks (2026-10-15): who is looking, for the "Support
// Tasks" section (tasks.js's canCreateTaskFromContact()).
export default function ManualActionsSidebar({ conversationId, onActionCompleted, onModalOpen, isAdmin = false, orderSummaryCustomerEmail = null, taskViewer = null, canCreateTasks = false, onTaskCreated = null }) {
  const { handleOpenChange, offsetIndexFor } = useModalStagger()
  // This component has no other awareness of the open conversation's channel
  // (only its id) — Shell (App.jsx), which renders this sidebar, doesn't hold
  // a lookup of loaded conversation objects by id either, so a small
  // self-contained fetch is simplest, same pattern OtherMessagesModal already
  // uses. Gates the eBay Seller Central section below (Create/Issue Refund
  // make no sense on a channel with no eBay side, e.g. M365 email). client
  // gates Refund Freight/Refund Tax below the same way (Website-account-only).
  const [channel, setChannel] = useState(null)
  const [client, setClient] = useState(null)
  const [isTestingPeriodTrqRole, setIsTestingPeriodTrqRole] = useState(false)
  const [erpOrderId, setErpOrderId] = useState(null)
  const [conversation, setConversation] = useState(null)
  const [creatingTask, setCreatingTask] = useState(false)
  const [createdTask, setCreatedTask] = useState(null)
  useEffect(() => {
    setConversation(null)
    setCreatingTask(false)
    if (!conversationId) {
      setChannel(null); setClient(null); setIsTestingPeriodTrqRole(false); setErpOrderId(null)
      return
    }
    fetchConversation(conversationId)
      .then((c) => {
        setConversation(c)
        setChannel(c.channel)
        setClient(c.client)
        setIsTestingPeriodTrqRole(c.trqClientCode === '1AW' || c.trqClientCode === 'AMW')
        setErpOrderId(c.erpOrderId ?? null)
      })
      .catch(() => { setChannel(null); setClient(null); setIsTestingPeriodTrqRole(false); setErpOrderId(null) })
  }, [conversationId])
  function onOpenChangeFor(key) {
    return (isOpen) => {
      handleOpenChange(key, isOpen)
      if (isOpen) onModalOpen?.()
    }
  }

  if (!conversationId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">Select a conversation to use manual actions.</Typography>
      </Box>
    )
  }
  // Add Note to Order renders last — after Create Return Shipping Label —
  // rather than in its MANUAL_ACTION_BUTTONS array position, so it reads as
  // the catch-all it is.
  const addNoteButton = MANUAL_ACTION_BUTTONS.find((def) => def.type === 'add_order_note')
  const otherButtons = MANUAL_ACTION_BUTTONS
    .filter((def) => def.type !== 'add_order_note')
    .filter((def) => !['refund_freight_trq', 'refund_tax_trq'].includes(def.type) || client === 'Website')
  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={700} sx={{ mb: 1.5 }}>Didn't find the proposed step you were looking for?</Typography>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, letterSpacing: 0.5 }}>Console / OMS</Typography>
      <Stack spacing={1}>
        {otherButtons.map((def) => (
          SAME_STEP_TYPES.has(def.type)
            ? <ManualStepRow key={`${conversationId}-${def.key}`} conversationId={conversationId} def={def} onActionCompleted={onActionCompleted} offsetIndex={offsetIndexFor(def.key)} onOpenChange={onOpenChangeFor(def.key)} />
            : def.type === 'cancel_order_trq'
            ? <CancelOrderGroupRow key={`${conversationId}-${def.key}`} conversationId={conversationId} def={def} onActionCompleted={onActionCompleted} isAdmin={isAdmin} offsetIndex={offsetIndexFor(def.key)} onOpenChange={onOpenChangeFor(def.key)} />
            : <ManualActionRow key={`${conversationId}-${def.key}`} conversationId={conversationId} def={def} onActionCompleted={onActionCompleted} isAdmin={isAdmin} offsetIndex={offsetIndexFor(def.key)} onOpenChange={onOpenChangeFor(def.key)} channel={channel} isTestingPeriodTrqRole={isTestingPeriodTrqRole} />
        ))}
        <CreateReturnLabelModal key={`${conversationId}-create_return_shipping_label`} conversationId={conversationId} onActionCompleted={onActionCompleted} offsetIndex={offsetIndexFor('create_return_shipping_label')} onOpenChange={onOpenChangeFor('create_return_shipping_label')} />
        <ResendConfirmationEmailModal key={`${conversationId}-resend_order_confirmation`} conversationId={conversationId} kind="order" erpOrderId={erpOrderId} prefillEmail={orderSummaryCustomerEmail} offsetIndex={offsetIndexFor('resend_order_confirmation')} onOpenChange={onOpenChangeFor('resend_order_confirmation')} />
        <ResendConfirmationEmailModal key={`${conversationId}-resend_shipping_confirmation`} conversationId={conversationId} kind="shipping" erpOrderId={erpOrderId} prefillEmail={orderSummaryCustomerEmail} offsetIndex={offsetIndexFor('resend_shipping_confirmation')} onOpenChange={onOpenChangeFor('resend_shipping_confirmation')} />
        <ManualActionRow key={`${conversationId}-${addNoteButton.key}`} conversationId={conversationId} def={addNoteButton} onActionCompleted={onActionCompleted} isAdmin={isAdmin} offsetIndex={offsetIndexFor(addNoteButton.key)} onOpenChange={onOpenChangeFor(addNoteButton.key)} isTestingPeriodTrqRole={isTestingPeriodTrqRole} />
      </Stack>
      {/* eBay orders only (2026-10-30): hidden for any other order. */}
      {messageCustomerAvailable(conversation) && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, letterSpacing: 0.5 }}>Customer</Typography>
          <Stack spacing={1}>
            <MessageCustomerModal key={`${conversationId}-message_customer`} conversationId={conversationId} onActionCompleted={onActionCompleted} offsetIndex={offsetIndexFor('message_customer')} onOpenChange={onOpenChangeFor('message_customer')} />
          </Stack>
        </>
      )}
      {/* Support Tasks (2026-10-15): a task from a Support contact, for
          whoever can create tasks; the form prefilled with its order number. */}
      {canCreateTaskFromContact(conversation, { viewer: taskViewer, canCreateTasks }) && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, letterSpacing: 0.5 }} data-testid="support-tasks-heading">Support Tasks</Typography>
          <Button variant="outlined" size="small" fullWidth onClick={() => setCreatingTask(true)} data-testid="create-task-from-contact">Create Task</Button>
          <CreateTaskDialog
            open={creatingTask}
            onClose={() => setCreatingTask(false)}
            initialValues={conversation?.erpOrderId ? { orderDetails: conversation.erpOrderId } : null}
            sourceConversationId={conversation?.id ?? null}
            onCreated={(task) => { setCreatingTask(false); setCreatedTask(task); onTaskCreated?.(task) }}
          />
        </>
      )}
      <TaskCreatedSnackbar task={createdTask} onClose={() => setCreatedTask(null)} />
      <Stack spacing={1}>
      </Stack>
      {(channel === 'ebay' || (channel === 'm365_email' && client === 'eBay')) && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, letterSpacing: 0.5 }}>eBay Seller Central</Typography>
          <Stack spacing={1}>
            {EBAY_SELLER_CENTRAL_BUTTONS.map((def) => (
              <RefundModal
                key={`${conversationId}-${def.key}`}
                conversationId={conversationId}
                onActionCompleted={onActionCompleted}
                actionType={def.actionType}
                label={def.label}
                instructionalText={def.instructionalText}
                requiresReturnId={def.requiresReturnId}
                offsetIndex={offsetIndexFor(def.key)}
                onOpenChange={onOpenChangeFor(def.key)}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  )
}
